import React, { useState } from 'react'
import classNames from 'classnames'

import css from './RegItemCard.styles.scss'

import { RegItemCardProps } from './RegItemCard.types'
import { BuyingOptions } from './components/BuyingOptions/BuyingOptions'
import { RegItemControls } from './components/Controls/Controls'
import { ProductDescription } from './components/ProductDescription/ProductDescription'
import { ProductNotes } from './components/ProductNotes/ProductNotes'
import { DragHandle, UserOutline } from 'baby-design/icons'
import { Image } from 'baby-design'

const getScreenSize = () => {
  if (window.innerWidth < 768) {
    return 'phone'
  } else if (window.innerWidth < 1224) {
    return 'tablet'
  } else {
    return 'desktop'
  }
}

const RegItemCard: React.FC<RegItemCardProps> = ({
  addedByText,
  product,
  categories = [],
  size,
  onCategoryChange = () => {},
  onEditClick = () => {},
  onDeleteClick = () => {},
  onBuyingOptionClick = () => {},
  badge,
  className,
  inSaleUIFeature,
  ...props
}) => {
  const isDraggingEnabled = props.draggable === true
  const [screenSize, setScreenSize] = useState(
    (size || getScreenSize()) as 'desktop' | 'tablet' | 'phone'
  )

  window.addEventListener('resize', () => {
    setScreenSize(getScreenSize())
  })

  const RegItemCardClasses = classNames(
    css.RegItemCard,
    {
      [css.reg_item_card__desktop]: screenSize === 'desktop',
      [css.reg_item_card__tablet]: screenSize === 'tablet',
      [css.reg_item_card__phone]: screenSize === 'phone',
      [css.reg_item_card__is_draggable]: isDraggingEnabled,
    },
    className
  )
  const image = (
    <div className={css.reg_item_card__image_container}>
      <a href={product.url} target="_blank" aria-label="Link to purchase item">
        {product.image ? (
          <Image src={product.image} alt={product.heading} />
        ) : (
          <div className={css.reg_item_card__image_placeholder} />
        )}
        {badge && <div className={css.reg_item_card__image_badge}>{badge}</div>}
      </a>
    </div>
  )

  const description = (
    <>
      <ProductDescription size={screenSize} product={product} />
      {addedByText && (
        <div className={css.reg_item_card__added_by}>
          <UserOutline />
          <span>{addedByText}</span>
        </div>
      )}
    </>
  )
  const controls = (
    <RegItemControls
      size={screenSize}
      defaultCategory={product.category}
      categories={categories}
      onCategoryChange={onCategoryChange}
      onEditClick={onEditClick}
      onDeleteClick={onDeleteClick}
    />
  )

  if (screenSize === 'desktop') {
    return (
      <div
        className={RegItemCardClasses}
        ref={props.innerRef}
        {...props.draggableProps}
        {...props}
      >
        <div className={css.reg_item_card__content}>
          <div {...props.dragHandleProps} className={css.reg_item_card__image}>
            {image}
          </div>
          <div
            {...props.dragHandleProps}
            className={css.reg_item_card__description}
          >
            {description}
          </div>
          <div className={css.reg_item_card__options}>
            {product.offers && product.offers.length > 0 && (
              <BuyingOptions
                onBuyingOptionClick={onBuyingOptionClick}
                isGiftCard={product.isGiftCard || false}
                options={product.offers}
                inSaleUIFeature={inSaleUIFeature}
              />
            )}
            {controls}
          </div>
        </div>
        {isDraggingEnabled && (
          <div
            {...props.dragHandleProps}
            className={css.reg_item_card__drag_handle}
            aria-label="Drag Handle"
          >
            <DragHandle />
          </div>
        )}
      </div>
    )
  } else if (screenSize === 'tablet') {
    return (
      <div
        className={RegItemCardClasses}
        ref={props.innerRef}
        isDragDisabled={true}
        {...props.draggableProps}
        {...props}
        tabIndex={-1}
      >
        <div className={css.reg_item_card__content}>
          <div className={css.reg_item_card__image}>{image}</div>
          <div className={css.reg_item_card__description}>
            <span>{description}</span>
            {controls}
          </div>
          {product.notes && (
            <div className={css.reg_item_card__notes}>
              <ProductNotes notes={product.notes} />
            </div>
          )}
        </div>
      </div>
    )
  } else {
    return (
      <div
        className={RegItemCardClasses}
        ref={props.innerRef}
        isDragDisabled={true}
        {...props.draggableProps}
        {...props}
        tabIndex={-1}
      >
        <div className={css.reg_item_card__content}>
          <div className={css.reg_item_card__image}>{image}</div>
          <div className={css.reg_item_card__description}>
            <span>{description}</span>
            {controls}
          </div>
          {product.notes && (
            <div className={css.reg_item_card__notes}>
              <ProductNotes notes={product.notes} />
            </div>
          )}
        </div>
      </div>
    )
  }
}

export default RegItemCard
